import React, { useState, useEffect } from 'react';
import { Plus, CreditCard as Edit, Trash2, Building2, User, Calendar, Eye, EyeOff, RefreshCw } from 'lucide-react';
import { Project } from '../../types/collaborators';
import { ExtendedProject } from '../../types/budgetSync';
import { projectsService, teamMembersService, supabase } from '../../services/supabaseService';
import { notificationService } from '../../services/notificationService';
import { emailNotificationService } from '../../services/emailNotificationService';
import { structuredLogger } from '../../services/structuredLogger';
import ProjectModalExtended from './ProjectModalExtended';
import ProjectDetailPage from '../projects/ProjectDetailPage';
import { usePermissions } from '../../hooks/usePermissions';

interface ProjectsListProps {
  onSelectProject?: (projectId: string, projectName: string) => void;
  // Cuando se navega desde el dashboard del colaborador, entramos ya
  // directamente al detalle de un proyecto concreto.
  initialProjectId?: string | null;
  onProjectConsumed?: () => void;
}

const ProjectsList: React.FC<ProjectsListProps> = ({ onSelectProject, initialProjectId, onProjectConsumed }) => {
  const [projects, setProjects] = useState<ExtendedProject[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProject, setEditingProject] = useState<ExtendedProject | null>(null);
  const [detailProjectId, setDetailProjectId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  // Mapa projectId → count de partes de horas con status='submitted'
  // (pendientes de aprobar por el admin). Se muestra como badge rojo
  // sobre cada fila del listado para llamar la atención.
  const [pendingByProject, setPendingByProject] = useState<Record<string, number>>({});

  const showToast = (message: string, type: 'success' | 'error') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  };

  const { isCollaboradorProgramador, isSuperAdmin } = usePermissions();
  const canManageProjects = isSuperAdmin();
  const isProgrammerView = isCollaboradorProgramador();

  useEffect(() => {
    loadProjects();
  }, []);

  // Si nos llega initialProjectId desde el dashboard del colaborador,
  // abrimos el detalle directamente y limpiamos el prop.
  useEffect(() => {
    if (initialProjectId) {
      setDetailProjectId(initialProjectId);
      onProjectConsumed?.();
    }
  }, [initialProjectId]);

  const loadProjects = async () => {
    try {
      setLoading(true);
      const projectsData = await projectsService.getAll();
      setProjects(projectsData);
      await loadPendingCounts(projectsData);
    } catch (error) {
      structuredLogger.error('Error loading projects', { error });
      setProjects([]);
    } finally {
      setLoading(false);
    }
  };

  const loadPendingCounts = async (list: ExtendedProject[]) => {
    if (list.length === 0) return;
    try {
      const { data, error } = await supabase
        .from('timesheets')
        .select('project_id, id')
        .eq('status', 'submitted')
        .in('project_id', list.map(p => p.id));
      if (error) throw error;
      const counts: Record<string, number> = {};
      (data || []).forEach((row: any) => {
        counts[row.project_id] = (counts[row.project_id] || 0) + 1;
      });
      setPendingByProject(counts);
    } catch (err) {
      console.error('Error cargando partes pendientes por proyecto:', err);
    }
  };

  const handleCreateProject = () => {
    setEditingProject(null);
    setIsModalOpen(true);
  };

  const handleEditProject = (project: Project) => {
    setEditingProject(project);
    setIsModalOpen(true);
  };

  const handleSaveProject = (projectData: Partial<Project>) => {
    if (editingProject) {
      // Update existing
      updateProject(projectData);
    } else {
      // Create new
      createProject(projectData);
    }
  };

  const createProject = async (projectData: Partial<Project>) => {
    try {
      // El modal ExtendedProjectModal envía mezcla camelCase + snake_case.
      // Aquí normalizamos al contrato del service (camelCase).
      const d = projectData as any;
      const newProject = await projectsService.create({
        name: d.name || '',
        clientId: d.clientId || d.client_id || '',
        clientName: d.clientName || d.client_name || '',
        description: d.description,
        status: d.status || 'active',
        assignedUsers: d.assignedUsers || d.assigned_users || [],
        budget: d.budget,
        totalHours: d.total_hours ?? d.totalHours,
        hourlyRate: d.hourly_rate ?? d.hourlyRate,
        startDate: d.start_date || d.startDate || undefined,
        endDate: d.end_date || d.endDate || undefined,
        durationMonths: d.duration_months ?? d.durationMonths,
        techStack: d.tech_stack || d.techStack,
        milestones: d.milestones,
      });

      setProjects([newProject, ...projects]);
      setIsModalOpen(false);
      setEditingProject(null);
      showToast('Proyecto creado correctamente', 'success');

      // Al crear un proyecto NO notificamos a admins (poco valor).
      // En cambio, sí notificamos a los colaboradores que se asignan en
      // ese acto, para que sepan que tienen trabajo nuevo.
      const newAssignees = projectData.assignedUsers || [];
      await notifyAssignees(newAssignees, {
        projectId: newProject.id,
        projectName: projectData.name || 'Proyecto',
        clientName: projectData.clientName || '',
      });
    } catch (error) {
      structuredLogger.error('Error creating project', { error });
      showToast('Error al crear el proyecto', 'error');
    }
  };

  const updateProject = async (projectData: Partial<Project>) => {
    if (!editingProject) return;

    try {
      const d = projectData as any;
      const updatedProject = await projectsService.update(editingProject.id, {
        name: d.name,
        clientId: d.clientId || d.client_id,
        clientName: d.clientName || d.client_name,
        description: d.description,
        status: d.status,
        assignedUsers: d.assignedUsers || d.assigned_users || [],
        budget: d.budget,
        totalHours: d.total_hours ?? d.totalHours,
        hourlyRate: d.hourly_rate ?? d.hourlyRate,
        startDate: d.start_date || d.startDate || undefined,
        endDate: d.end_date || d.endDate || undefined,
        durationMonths: d.duration_months ?? d.durationMonths,
        techStack: d.tech_stack || d.techStack,
        milestones: d.milestones,
      });

      // Diff de asignaciones — sólo notificamos a los NUEVOS.
      const previous = (editingProject.assignedUsers as string[]) || [];
      const nextAssignees = projectData.assignedUsers || [];
      const added = nextAssignees.filter((id: string) => !previous.includes(id));

      setProjects(projects.map(p =>
        p.id === editingProject.id ? updatedProject : p
      ));
      setIsModalOpen(false);
      setEditingProject(null);
      showToast('Proyecto actualizado correctamente', 'success');

      // NO enviamos notificación "Proyecto Actualizado" al equipo: cambios
      // ordinarios (renombres, ajuste de fechas, presupuesto) son ruido.
      // Sí notificamos a los nuevos asignados con "te asignaron al proyecto".
      if (added.length > 0) {
        await notifyAssignees(added, {
          projectId: editingProject.id,
          projectName: projectData.name || editingProject.name,
          clientName: projectData.clientName || editingProject.clientName || '',
        });
      }
    } catch (error) {
      structuredLogger.error('Error updating project', { error });
      showToast('Error al actualizar el proyecto', 'error');
    }
  };

  // Envía notificación in-app + email a cada colaborador asignado.
  const notifyAssignees = async (
    userIds: string[],
    ctx: { projectId: string; projectName: string; clientName: string }
  ) => {
    if (userIds.length === 0) return;

    const memberList = await teamMembersService.getAll();
    const membersById = new Map(memberList.map((m: any) => [m.id, m]));

    await Promise.all(userIds.map(async (userId) => {
      const member = membersById.get(userId);

      // 1) Notificación in-app targeted (aparece en la campana del usuario).
      try {
        await notificationService.createNotification({
          user_id: userId,
          code: 'PROJECT.ASSIGNED',
          title: 'Nueva asignación',
          body: `Te asignaron al proyecto ${ctx.projectName}${ctx.clientName ? ` (${ctx.clientName})` : ''}`,
          severity: 'info',
          data: { project_id: ctx.projectId, project_name: ctx.projectName }
        });
      } catch (err) {
        console.error('Error notif in-app:', err);
      }

      // 2) Email al colaborador si tiene email y Resend está configurado.
      if (member?.email) {
        try {
          await emailNotificationService.sendProjectAssignedEmail(
            member.name || 'Colaborador',
            member.email,
            ctx.projectName,
            ctx.clientName,
            `${window.location.origin}/pagos`
          );
        } catch (err) {
          console.error('Error email asignación:', err);
        }
      }
    }));
  };

  const handleToggleStatus = (projectId: string) => {
    const project = projects.find(p => p.id === projectId);
    if (project) {
      const newStatus = project.status === 'active' ? 'paused' : 'active';
      updateProjectStatus(projectId, newStatus);
    }
  };

  const updateProjectStatus = async (projectId: string, status: Project['status']) => {
    try {
      await projectsService.update(projectId, { status });

      setProjects(prev => prev.map(p =>
        p.id === projectId ? { ...p, status } : p
      ));

      const project = projects.find(p => p.id === projectId);
      const statusLabels: Record<Project['status'], string> = { active: 'Activo', completed: 'Completado', paused: 'Pausado' };
      await notificationService.createNotification({
        code: 'PROJECT.STATUS_CHANGED',
        title: 'Estado de Proyecto Cambiado',
        body: `${project?.name} está ahora: ${statusLabels[status]}`,
        severity: 'info',
        data: { project_id: projectId, status }
      });
    } catch (error) {
      structuredLogger.error('Error updating project status', { error });
      showToast('Error al actualizar el estado del proyecto', 'error');
    }
  };

  const handleDeleteProject = async (projectId: string) => {
    const project = projects.find(p => p.id === projectId);
    if (project && window.confirm(`¿Eliminar el proyecto "${project.name}"? Esta acción no se puede deshacer.`)) {
      try {
        await projectsService.delete(projectId);
        setProjects(projects.filter(p => p.id !== projectId));
        showToast('Proyecto eliminado correctamente', 'success');

        await notificationService.createNotification({
          code: 'PROJECT.DELETED',
          title: 'Proyecto Eliminado',
          body: `El proyecto "${project.name}" ha sido eliminado`,
          severity: 'warn',
          data: { project_name: project.name }
        });
      } catch (error) {
        structuredLogger.error('Error deleting project', { error });
        showToast('Error al eliminar el proyecto', 'error');
      }
    }
  };

  const getStatusColor = (status: Project['status']) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'completed': return 'bg-blue-100 text-blue-800';
      case 'paused': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status: Project['status']) => {
    switch (status) {
      case 'active': return 'Activo';
      case 'completed': return 'Completado';
      case 'paused': return 'Pausado';
      default: return status;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-3">Cargando proyectos...</span>
      </div>
    );
  }

  if (detailProjectId) {
    return (
      <ProjectDetailPage
        projectId={detailProjectId}
        onBack={() => { setDetailProjectId(null); loadProjects(); }}
      />
    );
  }

  return (
    <div className="relative">
      {/* Toast */}
      {toast && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg text-sm font-medium transition-all ${
          toast.type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
        }`}>
          {toast.message}
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Proyectos</h2>
          <p className="text-gray-600">
            {isProgrammerView ? 'Proyectos en los que estás asignado' : 'Gestiona los proyectos donde trabajan los colaboradores'}
          </p>
        </div>
        {canManageProjects && (
          <button
            onClick={handleCreateProject}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span>Nuevo Proyecto</span>
          </button>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <div className="bg-green-50 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-green-600">Proyectos Activos</p>
              <p className="text-2xl font-bold text-green-900">
                {projects.filter(p => p.status === 'active').length}
              </p>
            </div>
            <div className="p-3 bg-green-100 rounded-lg">
              <Eye className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-blue-50 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-blue-600">Proyectos Completados</p>
              <p className="text-2xl font-bold text-blue-900">
                {projects.filter(p => p.status === 'completed').length}
              </p>
            </div>
            <div className="p-3 bg-blue-100 rounded-lg">
              <Building2 className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-yellow-50 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-yellow-600">Proyectos Pausados</p>
              <p className="text-2xl font-bold text-yellow-900">
                {projects.filter(p => p.status === 'paused').length}
              </p>
            </div>
            <div className="p-3 bg-yellow-100 rounded-lg">
              <EyeOff className="w-6 h-6 text-yellow-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Projects Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Proyecto
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Cliente
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Estado
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Creado
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {projects.map((project) => (
                <tr key={project.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        <div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center">
                          {project.external_id ? (
                            <RefreshCw className="w-5 h-5 text-blue-600" />
                          ) : (
                            <Building2 className="w-5 h-5 text-blue-600" />
                          )}
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="flex items-center space-x-2">
                          <span className="text-sm font-medium text-gray-900">{project.name}</span>
                          {project.external_id && (
                            <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 rounded">
                              Sincronizado
                            </span>
                          )}
                          {pendingByProject[project.id] > 0 && (
                            <span
                              className="px-2 py-0.5 text-xs font-semibold bg-red-500 text-white rounded-full"
                              title={`${pendingByProject[project.id]} partes de horas pendientes de aprobar`}
                            >
                              {pendingByProject[project.id]} pendiente{pendingByProject[project.id] !== 1 ? 's' : ''}
                            </span>
                          )}
                        </div>
                        {project.description && (
                          <div className="text-sm text-gray-500">{project.description}</div>
                        )}
                        {canManageProjects && project.budget != null && (
                          <div className="text-xs text-gray-500 mt-1">
                            Presupuesto: €{project.budget.toLocaleString()} | {project.total_hours}h
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      <User className="w-4 h-4 text-gray-400" />
                      <span className="text-sm text-gray-900">{project.clientName}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <button
                      disabled={!canManageProjects}
                      onClick={() => handleToggleStatus(project.id)}
                      className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(project.status)}`}
                    >
                      {getStatusLabel(project.status)}
                    </button>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      <Calendar className="w-4 h-4 text-gray-400" />
                      <span className="text-sm text-gray-500">
                        {project.createdAt.toLocaleDateString()}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end space-x-2">
                      <button
                        onClick={() => setDetailProjectId(project.id)}
                        className="text-blue-600 hover:text-blue-900 hover:bg-blue-50 p-1 rounded transition-colors"
                        title="Ver detalle"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      {canManageProjects && (
                        <>
                          <button
                            onClick={() => handleEditProject(project)}
                            className="text-gray-600 hover:text-gray-900 p-1 rounded"
                            title="Editar (datos básicos)"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteProject(project.id)}
                            className="text-red-600 hover:text-red-900 p-1 rounded"
                            title="Eliminar proyecto"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {projects.length === 0 && (
          <div className="text-center py-12">
            <Building2 className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No hay proyectos</h3>
            <p className="text-gray-500 mb-6">Comienza creando tu primer proyecto</p>
            <button
              onClick={handleCreateProject}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors mx-auto"
            >
              <Plus className="w-4 h-4" />
              <span>Nuevo Proyecto</span>
            </button>
          </div>
        )}
      </div>

      {/* Modal */}
      <ProjectModalExtended
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingProject(null);
        }}
        onSave={handleSaveProject}
        project={editingProject}
      />
    </div>
  );
};

export default ProjectsList;